import { __decorate } from "tslib";
import { attr, observable } from '@microsoft/fast-element';
import { FoundationElement } from '@microsoft/fast-foundation';
import { foundationFilterStyles as styles } from './filter.styles';
import { foundationFilterTemplate as template } from './filter.template';
export var FilterEvents;
(function (FilterEvents) {
    FilterEvents["change"] = "filter-changed";
    FilterEvents["clear"] = "filter-cleared";
})(FilterEvents || (FilterEvents = {}));
/**
 * @tagname %%prefix%%-filter
 */
export class Filter extends FoundationElement {
    constructor() {
        super(...arguments);
        this.type = 'text';
        this.textFilter = '';
        this.enumFilterOptions = [{ name: 'All', value: 'all', selected: true }];
    }
    textFilterChanged(oldCriteria, newCriteria) {
        if (!(newCriteria === null || newCriteria === void 0 ? void 0 : newCriteria.length)) {
            // treat empty string as clear
            this.$emit(FilterEvents.clear, {
                fieldName: this.fieldName,
                target: this.target,
            });
            return;
        }
        const newFilter = `Expr.containsIgnoreCase(${this.fieldName},'${newCriteria}')`;
        this.$emit(FilterEvents.change, {
            fieldName: this.fieldName,
            filter: newFilter,
            target: this.target,
        });
    }
    numFilterMinChanged(oldMin, newMin) {
        this.numFilterMin = Number.isFinite(Number(newMin)) ? Number(newMin) : undefined;
        this.handleNumericFilter(this.numFilterMin, this.numFilterMax);
    }
    numFilterMaxChanged(oldMax, newMax) {
        this.numFilterMax = Number.isFinite(Number(newMax)) ? Number(newMax) : undefined;
        this.handleNumericFilter(this.numFilterMin, this.numFilterMax);
    }
    handleNumericFilter(min, max) {
        if (!Number.isFinite(min) && !Number.isFinite(max)) {
            this.$emit(FilterEvents.clear, {
                fieldName: this.fieldName,
                target: this.target,
            });
            return;
        }
        const minFilter = `${this.fieldName} >= ${min}`;
        const maxFilter = `${this.fieldName} <= ${max}`;
        const newFilter = !Number.isFinite(min)
            ? maxFilter
            : !Number.isFinite(max)
                ? minFilter
                : `(${minFilter} && ${maxFilter})`;
        this.$emit(FilterEvents.change, {
            fieldName: this.fieldName,
            filter: newFilter,
            target: this.target,
        });
    }
    /**
     * Example: 1634083200000 -> '20211013-00:00:00'
     * @param epoch ms since unix epoch
     * @returns date string compatible with genesis Expr filters
     */
    formatDate(epoch) {
        if (!Number.isFinite(epoch))
            return undefined;
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        return new Date(epoch).toISOString().slice(0, 19).replace(/-/g, '').replace('T', '-');
    }
    dateFilterMinChanged(oldMin, newMin) {
        this.dateMinMS = Date.parse(newMin);
        this.handleDateFilter(this.dateMinMS, this.dateMaxMS);
    }
    dateFilterMaxChanged(oldMax, newMax) {
        this.dateMaxMS = Date.parse(newMax);
        this.handleDateFilter(this.dateMinMS, this.dateMaxMS);
    }
    handleDateFilter(min, max) {
        if (!Number.isFinite(min) && !Number.isFinite(max)) {
            this.$emit(FilterEvents.clear, {
                fieldName: this.fieldName,
            });
            return;
        }
        const minFilter = `Expr.dateTimeIsAfter(${this.fieldName}, '${this.formatDate(min)}')`;
        const maxFilter = `Expr.dateTimeIsBefore(${this.fieldName}, '${this.formatDate(max)}')`;
        const newFilter = !Number.isFinite(min)
            ? maxFilter
            : !Number.isFinite(max)
                ? minFilter
                : `(${minFilter} && ${maxFilter})`;
        this.$emit(FilterEvents.change, {
            fieldName: this.fieldName,
            filter: newFilter,
            target: this.target,
        });
    }
    connectedCallback() {
        var _a;
        super.connectedCallback();
        if ((_a = this.enumOptions) === null || _a === void 0 ? void 0 : _a.length) {
            const delimiter = this.enumOptions.includes('|') ? '|' : ','; // TODO: confirm delimiter
            const newOptions = this.enumOptions
                .split(delimiter)
                .map((opt) => ({ name: opt, value: opt }));
            this.enumFilterOptions = [{ name: 'All', value: 'all', selected: true }, ...newOptions];
        }
    }
    clear() {
        switch (this.type) {
            case 'number':
                this.numFilterFieldMin.value = undefined;
                this.numFilterFieldMax.value = undefined;
                this.numFilterMin = undefined;
                this.numFilterMax = undefined;
                break;
            case 'date':
                this.dateFilterFieldMin.value = '';
                this.dateFilterFieldMax.value = '';
                this.dateMinMS = NaN;
                this.dateMaxMS = NaN;
                break;
            case 'text':
                this.textFilterField.value = '';
        }
        this.$emit(FilterEvents.clear, {
            fieldName: this.fieldName,
            target: this.target,
        });
    }
    onBooleanFilterSelection(selection) {
        if (!selection || selection === 'all') {
            this.$emit(FilterEvents.clear, {
                fieldName: this.fieldName,
                target: this.target,
            });
            return;
        }
        this.$emit(FilterEvents.change, {
            fieldName: this.fieldName,
            filter: `${this.fieldName} == ${selection}`,
            target: this.target,
        });
    }
    onEnumFilterSelection(selection) {
        if (!selection || selection === 'all') {
            this.$emit(FilterEvents.clear, {
                fieldName: this.fieldName,
                target: this.target,
            });
            return;
        }
        this.$emit(FilterEvents.change, {
            fieldName: this.fieldName,
            filter: `${this.fieldName} == "${selection}"`,
            target: this.target,
        });
    }
}
__decorate([
    attr
], Filter.prototype, "fieldName", void 0);
__decorate([
    attr
], Filter.prototype, "type", void 0);
__decorate([
    attr
], Filter.prototype, "label", void 0);
__decorate([
    attr
], Filter.prototype, "target", void 0);
__decorate([
    observable
], Filter.prototype, "textFilter", void 0);
__decorate([
    observable
], Filter.prototype, "numFilterMin", void 0);
__decorate([
    observable
], Filter.prototype, "numFilterMax", void 0);
__decorate([
    observable
], Filter.prototype, "dateFilterMin", void 0);
__decorate([
    observable
], Filter.prototype, "dateFilterMax", void 0);
__decorate([
    attr
], Filter.prototype, "enumOptions", void 0);
__decorate([
    observable
], Filter.prototype, "enumFilterOptions", void 0);
export const foundationFilter = Filter.compose({
    baseName: 'filter',
    template,
    styles,
});
